import {
  Controller,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from 'react-hook-form'
import type { ReactNode } from 'react'

export type FormFieldRenderProps = {
  value: unknown
  onChange: (...event: unknown[]) => void
  onBlur: () => void
  errorMessage?: string
  name: string
}

export type FormFieldProps<T extends FieldValues = FieldValues> = {
  name: FieldPath<T>
  render: (props: FormFieldRenderProps) => ReactNode
}

/**
 * Connects any input component to the nearest Form context via react-hook-form's
 * Controller. Use the typed field components (TextField, SelectField, etc.) for
 * common cases — reach for FormField directly when you need a fully custom input.
 */
export const FormField = <T extends FieldValues = FieldValues>({
  name,
  render,
}: FormFieldProps<T>) => {
  const { control } = useFormContext<T>()

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <>
          {render({
            value: field.value,
            onChange: field.onChange,
            onBlur: field.onBlur,
            errorMessage: fieldState.error?.message,
            name: field.name,
          })}
        </>
      )}
    />
  )
}
